import { useEffect, useMemo, useState } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { toast } from "sonner";
import { Receipt, Ticket, LayoutList, Download, FileSpreadsheet, Pencil, History } from "lucide-react";
import CancelTicketButton from "./admin/CancelTicketButton";

/**
 * MonthlyDocumentsPanel — admin dashboard widget summarising invoices +
 * tickets emitted in a chosen month, with a segmented filter to isolate one
 * kind or view both. Renders a detail table below the summary chips.
 *
 * Endpoint: GET /api/admin/documents/monthly?year=&month=&kind=
 */
const MONTHS_ES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const KIND_TABS = [
  { key: "all", label: "Todos", icon: LayoutList },
  { key: "invoice", label: "Facturas", icon: Receipt },
  { key: "ticket", label: "Tickets", icon: Ticket },
];

const fmtEur = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;

const FIELD_LABELS = {
  manual_concept: "Concepto",
  amount_eur: "Importe",
  vat_percent_override: "IVA (%)",
};

const fmtDiffValue = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "number") return String(v).replace(".", ",");
  return String(v);
};
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

export default function MonthlyDocumentsPanel() {
  const { user: currentUser } = useAuth();
  const isSuperadmin = currentUser?.role === "superadmin";
  // Document visibility mirrors the backend's ``_doc_visibility``:
  // superadmin sees both kinds, admin invoices only, and gestión whatever the
  // superadmin granted via the view_invoices / view_tickets perms.
  const perms = currentUser?.perms || {};
  const canSeeInvoices =
    isSuperadmin || currentUser?.role === "admin" || !!perms.view_invoices;
  const canSeeTickets = isSuperadmin || !!perms.view_tickets;
  const canSeeAnything = canSeeInvoices || canSeeTickets;
  const kindTabs = useMemo(
    () =>
      KIND_TABS.filter(
        (t) =>
          (t.key !== "ticket" || canSeeTickets) &&
          (t.key !== "invoice" || canSeeInvoices) &&
          // "Todos" only makes sense when both kinds are visible.
          (t.key !== "all" || (canSeeInvoices && canSeeTickets)),
      ),
    [canSeeTickets, canSeeInvoices],
  );
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  // Start on a tab this user actually has: "all" only when both kinds are
  // visible, otherwise the single kind they can see.
  const [kind, setKind] = useState(
    canSeeInvoices && canSeeTickets ? "all" : canSeeInvoices ? "invoice" : "ticket",
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // {id, concept, amount_eur, vat_percent}
  const [editForm, setEditForm] = useState({ concept: "", amount: "", vat_percent: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [auditLog, setAuditLog] = useState(null); // null=collapsed, []=loaded-empty, [...]=loaded
  const [onlyEdited, setOnlyEdited] = useState(false);

  const yearOptions = useMemo(() => {
    const cur = now.getFullYear();
    return [cur, cur - 1, cur - 2];
  }, [now]);

  const load = () => {
    setLoading(true);
    return api
      .get(`/admin/documents/monthly?year=${year}&month=${month}&kind=${kind}`)
      .then(({ data: d }) => { setData(d); })
      .catch(() => { setData(null); })
      .finally(() => { setLoading(false); });
  };

  // Keep the selected tab within what this user may see (perms can change
  // between sessions, and "all" is invalid when only one kind is visible).
  useEffect(() => {
    if (!canSeeAnything) return;
    const valid = kindTabs.some((t) => t.key === kind);
    if (!valid) setKind(canSeeInvoices ? "invoice" : "ticket");
  }, [canSeeAnything, canSeeInvoices, kind, kindTabs]);

  useEffect(() => {
    if (!canSeeAnything) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month, kind, canSeeAnything]);

  const downloadPdf = async (item) => {
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/invoices/${item.id}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const prefix = item.document_kind === "ticket" ? "ticket" : "factura";
    a.download = `${prefix}-${item.invoice_number || item.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadOriginalTicket = async (item) => {
    // The ticket handed to the customer before this row was promoted to an
    // invoice. Kept for consultation; it can no longer be cancelled.
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/tx/${item.id}/original-ticket`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      toast.error("No se pudo descargar el ticket original");
      return;
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ticket-${item.original_ticket_number || item.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const downloadAutogenInvoice = async (item) => {
    // Streams the "hidden" auto-generated invoice for a Stripe purchase
    // (SA/admin only — the alumno never sees this file).
    const token = localStorage.getItem("token");
    const res = await fetch(`${API}/admin/tx/${item.id}/autogen-invoice`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `factura-${item.autogen_invoice_number || item.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("token");
    const res = await fetch(
      `${API}/admin/documents/monthly.csv?year=${year}&month=${month}&kind=${kind}`,
      { headers: { Authorization: `Bearer ${token}` } },
    );
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `documentos-${year}-${String(month).padStart(2, "0")}-${kind}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const summary = data?.summary;
  const allItems = data?.items || [];
  const items = onlyEdited ? allItems.filter((it) => it.has_edits) : allItems;
  const hiddenCount = allItems.length - items.length;

  // A gestión account with neither document perm doesn't see this block at
  // all (the backend would 403 anyway) — better an absent panel than an
  // empty one that looks broken.
  if (!canSeeAnything) return null;

  return (
    <Card
      className="p-6 border border-gray-200 shadow-none rounded-lg mb-8"
      data-testid="monthly-docs-panel"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 text-brand-ink">
            <Receipt className="w-5 h-5" />
            <h2 className="font-display text-lg">Resumen mensual · documentos emitidos</h2>
          </div>
          <p className="text-sm text-gray-500 mt-0.5">
            Facturas y tickets cobrados en el mes seleccionado.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={month}
            onChange={(e) => setMonth(Number(e.target.value))}
            className="border rounded px-3 py-1.5 text-sm bg-white"
            data-testid="monthly-docs-month"
          >
            {MONTHS_ES.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="border rounded px-3 py-1.5 text-sm bg-white"
            data-testid="monthly-docs-year"
          >
            {yearOptions.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            data-testid="monthly-docs-export-csv"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Exportar CSV
          </Button>
        </div>
      </div>

      {/* Segmented filter */}
      <div className="inline-flex rounded-md border border-gray-200 bg-gray-50 p-0.5 mb-4" role="tablist">
        {kindTabs.map((t) => {
          const active = kind === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setKind(t.key)}
              className={`px-3 py-1.5 text-xs font-medium rounded flex items-center gap-1.5 transition-colors ${
                active ? "bg-white shadow-sm text-brand-ink" : "text-gray-500 hover:text-gray-700"
              }`}
              data-testid={`monthly-docs-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {isSuperadmin && (
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setOnlyEdited((v) => !v)}
            className={`inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border transition-colors ${
              onlyEdited
                ? "bg-amber-500 text-white border-amber-500 shadow-sm shadow-amber-500/20"
                : "bg-white text-gray-600 border-gray-200 hover:border-amber-400 hover:text-amber-700"
            }`}
            data-testid="monthly-docs-only-edited-toggle"
          >
            <History className="w-3 h-3" />
            Solo editadas
          </button>
          {onlyEdited && hiddenCount > 0 && (
            <span className="text-xs text-gray-500" data-testid="monthly-docs-filter-count">
              Mostrando {items.length} de {allItems.length}
            </span>
          )}
        </div>
      )}

      {/* Summary sentence */}
      {loading ? (
        <p className="text-sm text-gray-400 italic">Cargando…</p>
      ) : summary ? (
        <div className="text-sm text-brand-ink mb-4 leading-relaxed" data-testid="monthly-docs-summary">
          En <span className="font-semibold">{MONTHS_ES[month - 1]} {year}</span> has emitido{" "}
          {(kind === "all" || kind === "invoice") && canSeeInvoices && (
            <>
              <span className="font-mono-data text-brand" data-testid="monthly-docs-invoices-count">
                {summary.invoices.count}
              </span>{" "}
              facturas por{" "}
              <span className="font-mono-data text-brand" data-testid="monthly-docs-invoices-total">
                {fmtEur(summary.invoices.total_eur)}
              </span>
              {kind === "all" && canSeeTickets && " y "}
            </>
          )}
          {(kind === "all" || kind === "ticket") && canSeeTickets && (
            <>
              <span className="font-mono-data text-sky-700" data-testid="monthly-docs-tickets-count">
                {summary.tickets.count}
              </span>{" "}
              tickets por{" "}
              <span className="font-mono-data text-sky-700" data-testid="monthly-docs-tickets-total">
                {fmtEur(summary.tickets.total_eur)}
              </span>
            </>
          )}
          .
        </div>
      ) : null}

      {/* Table */}
      {!loading && items.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="monthly-docs-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-gray-500 border-b">
                <th className="text-left py-2">Nº</th>
                <th className="text-left py-2">Tipo</th>
                <th className="text-left py-2">Fecha</th>
                <th className="text-left py-2">Alumno</th>
                <th className="text-left py-2">Concepto</th>
                <th className="text-right py-2">Importe</th>
                <th className="text-right py-2">Origen</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr
                  key={it.id}
                  className={`border-b border-gray-100 hover:bg-gray-50 ${
                    it.status === "cancelled" ? "opacity-60" : ""
                  }`}
                  data-testid={`monthly-docs-row-${it.id}`}
                >
                  <td className="py-2 font-mono-data text-brand-ink whitespace-nowrap">
                    <span
                      className={`inline-flex items-center gap-1.5 ${
                        it.status === "cancelled" ? "line-through" : ""
                      }`}
                      title={it.has_edits ? "Factura editada — abre para ver historial" : ""}
                    >
                      {it.invoice_number || "—"}
                      {it.has_edits && (
                        <History
                          className="w-3 h-3 text-amber-600"
                          data-testid={`monthly-docs-edited-flag-${it.id}`}
                          aria-label="Factura editada"
                        />
                      )}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1 flex-wrap">
                      {it.document_kind === "ticket" ? (
                        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">TICKET</span>
                      ) : (
                        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-brand-soft text-brand-ink border border-[#bcdfd9]">FACTURA</span>
                      )}
                      {it.status === "cancelled" && (
                        <span
                          className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-red-50 text-red-700 border border-red-200"
                          title={`Anulado${it.cancelled_by_email ? ` por ${it.cancelled_by_email}` : ""}${
                            it.cancelled_at ? ` el ${fmtDate(it.cancelled_at)}` : ""
                          }`}
                          data-testid={`monthly-docs-cancelled-${it.id}`}
                        >
                          ANULADO
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-2 text-gray-600 whitespace-nowrap">{fmtDate(it.paid_at)}</td>
                  <td className="py-2 text-gray-700 truncate max-w-[180px]" title={it.user_email || ""}>
                    {it.user_name || it.user_email || "—"}
                  </td>
                  <td className="py-2 max-w-xs text-gray-700">
                    <p className="truncate" title={it.concept}>{it.concept}</p>
                    {it.voids_ticket_number && (
                      <p className="text-[10px] text-red-700 mt-0.5" data-testid={`monthly-docs-voids-${it.id}`}>
                        Anula al ticket {it.voids_ticket_number}
                      </p>
                    )}
                    {it.status === "cancelled" && (
                      <p
                        className="text-[10px] text-red-700 mt-0.5 whitespace-normal"
                        data-testid={`monthly-docs-cancelled-by-${it.id}`}
                      >
                        Anulado por {it.cancelled_by_email || "—"}
                        {it.cancelled_at ? ` · ${fmtDate(it.cancelled_at)}` : ""}
                        {it.voided_by_ticket_number ? ` · ticket de anulación ${it.voided_by_ticket_number}` : ""}
                      </p>
                    )}
                  </td>
                  <td className="py-2 text-right font-mono-data">{fmtEur(it.amount_eur)}</td>
                  <td className="py-2 text-right">
                    {it.is_manual ? (
                      <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">manual</span>
                    ) : (
                      <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-gray-50 text-gray-700 border border-gray-200">online</span>
                    )}
                  </td>
                  <td className="py-2 text-right">
                    {isSuperadmin && it.document_kind !== "ticket" && it.status !== "cancelled" && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditing(it);
                          setAuditLog(null);
                          setEditForm({
                            concept: it.concept || "",
                            amount: String(it.amount_eur ?? ""),
                            vat_percent: it.vat_percent != null ? String(it.vat_percent) : "",
                          });
                        }}
                        data-testid={`monthly-docs-edit-${it.id}`}
                        aria-label="Editar factura"
                        title="Editar factura"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    {it.has_original_ticket && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadOriginalTicket(it)}
                        title={`Descargar el ticket original ${it.original_ticket_number || ""} (ya no se puede anular)`}
                        data-testid={`monthly-docs-original-ticket-${it.id}`}
                      >
                        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-sky-50 text-sky-800 border border-sky-200">
                          TICKET
                        </span>
                      </Button>
                    )}
                    <CancelTicketButton item={it} onDone={load} />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => downloadPdf(it)}
                      data-testid={`monthly-docs-download-${it.id}`}
                      aria-label="Descargar PDF"
                    >
                      <Download className="w-3.5 h-3.5" />
                    </Button>
                    {it.document_kind === "ticket" && it.has_autogen_invoice && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadAutogenInvoice(it)}
                        data-testid={`monthly-docs-autogen-invoice-${it.id}`}
                        aria-label={`Descargar factura ${it.autogen_invoice_number}`}
                        title={`Descargar factura ${it.autogen_invoice_number} (oculta al alumno)`}
                      >
                        <span className="text-[10px] font-mono-data px-1.5 py-0.5 rounded bg-brand-soft text-brand-ink border border-[#bcdfd9]">
                          FACTURA
                        </span>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : !loading ? (
        <p className="text-sm text-gray-400 italic text-center py-6" data-testid="monthly-docs-empty">
          Sin documentos {onlyEdited ? "editados " : ""}emitidos en {MONTHS_ES[month - 1]} {year}.
        </p>
      ) : null}

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent data-testid="edit-invoice-dialog">
          <DialogHeader>
            <DialogTitle>Editar factura {editing?.invoice_number || ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="ei-concept">Concepto</Label>
              <Input
                id="ei-concept"
                value={editForm.concept}
                onChange={(e) => setEditForm({ ...editForm, concept: e.target.value })}
                data-testid="edit-invoice-concept"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="ei-amount">Importe (€)</Label>
                <Input
                  id="ei-amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.amount}
                  onChange={(e) => setEditForm({ ...editForm, amount: e.target.value })}
                  data-testid="edit-invoice-amount"
                />
              </div>
              <div>
                <Label htmlFor="ei-vat">IVA (%)</Label>
                <Input
                  id="ei-vat"
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={editForm.vat_percent}
                  onChange={(e) => setEditForm({ ...editForm, vat_percent: e.target.value })}
                  placeholder="0"
                  data-testid="edit-invoice-vat"
                />
              </div>
            </div>
            <p className="text-[11px] text-gray-500">
              El PDF de la factura se regenera automáticamente con los nuevos valores. Deja el IVA vacío para heredar el porcentaje global de Ajustes.
            </p>

            {editing && (
              <details
                className="rounded-md border border-gray-200 bg-gray-50 open:bg-white transition-colors"
                onToggle={async (e) => {
                  if (e.target.open && auditLog === null) {
                    try {
                      const { data } = await api.get(`/admin/invoices/${editing.id}/audit-log`);
                      setAuditLog(data);
                    } catch {
                      setAuditLog([]);
                    }
                  }
                }}
                data-testid="audit-log-toggle"
              >
                <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-brand-ink flex items-center gap-2">
                  <History className="w-3.5 h-3.5" /> Historial de cambios
                </summary>
                <div className="px-3 pb-3 pt-1 space-y-2" data-testid="audit-log-list">
                  {auditLog === null && (
                    <p className="text-xs text-gray-400 italic">Cargando…</p>
                  )}
                  {Array.isArray(auditLog) && auditLog.length === 0 && (
                    <p className="text-xs text-gray-400 italic">Sin cambios registrados aún.</p>
                  )}
                  {Array.isArray(auditLog) && auditLog.map((entry) => (
                    <div key={entry.id} className="border-l-2 border-brand pl-3 py-1">
                      <p className="text-[11px] text-gray-500 font-mono-data">
                        {new Date(entry.at).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" })}
                        {" · "}
                        <span className="text-brand-ink">{entry.edited_by_email}</span>
                      </p>
                      <ul className="mt-1 space-y-0.5">
                        {Object.entries(entry.changes || {}).map(([field, diff]) => (
                          <li key={field} className="text-xs text-gray-700">
                            <span className="font-medium">{FIELD_LABELS[field] || field}</span>:{" "}
                            <span className="line-through text-red-600 font-mono-data">{fmtDiffValue(diff.before)}</span>
                            {" → "}
                            <span className="text-emerald-700 font-mono-data">{fmtDiffValue(diff.after)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button
              onClick={async () => {
                if (!editing) return;
                const body = {};
                if (editForm.concept !== (editing.concept || "")) body.concept = editForm.concept;
                if (editForm.amount !== "" && Number(editForm.amount) !== editing.amount_eur) body.amount = Number(editForm.amount);
                if (editForm.vat_percent !== "" && Number(editForm.vat_percent) !== editing.vat_percent) body.vat_percent = Number(editForm.vat_percent);
                if (Object.keys(body).length === 0) {
                  toast.info("No hay cambios que guardar");
                  return;
                }
                setEditSaving(true);
                try {
                  await api.put(`/admin/invoices/${editing.id}`, body);
                  toast.success("Factura actualizada — PDF regenerado");
                  setAuditLog(null);
                  setEditing(null);
                  load();
                } catch (err) {
                  toast.error(err?.response?.data?.detail || "No se pudo guardar");
                } finally {
                  setEditSaving(false);
                }
              }}
              disabled={editSaving}
              className="bg-brand hover:bg-brand-hover text-white"
              data-testid="edit-invoice-save"
            >
              {editSaving ? "Guardando…" : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
